import { createComponentStore } from '../../utils/createComponentStore';
import {
  ensureCreationOutputPlaceholder,
  newCreationOutputPlaceholder,
} from '../../utils/creationOutputPlaceholder';
import { makeDefaultField } from '../pages/forms/fieldPreviews/fieldTypes';
import { normalizeFormCore, stripField } from '../pages/forms/fieldPreviews/normalizeFieldDefinitions';

/**
 * Default `form_step` component data. Matches FormStepComponentData.java: FormCore fields
 * (fields / title / description / accessPolicy / responseMode / formSettings) plus the three
 * flow-specific keys (stepperTitle, flowEntryId, flowConfig).
 *
 * `flowEntryId: null` + `flowConfig: null` = standalone form_step. FlowSettingsBlock flips
 * these to make this step either a flow entry or a member of an upstream flow.
 */
const emptyData = () => ({
  fields: [],
  title: '',
  description: '',
  accessPolicy: { mode: 'PUBLIC', selectedUserIds: [] },
  responseMode: 'FILL_SUBMIT',
  formSettings: {},
  stepperTitle: '',
  flowEntryId: null,
  flowConfig: null,
  // Creation-edge output placeholder. Author-renameable; auto-uniquified
  // so multiple form_step siblings never collide on the same `${formUrl}`.
  creationOutputPlaceholder: newCreationOutputPlaceholder('form_step'),
});

const { getStore: getFormStepStore, defaultStore } = createComponentStore({
  type: 'form_step',
  endpoint: 'form-step',
  registryKey: 'form_step',
  initData: emptyData,
  onLoadData: (rawData) => ({
    ...emptyData(),
    ...normalizeFormCore(rawData),
    stepperTitle: rawData?.stepperTitle || '',
    flowEntryId: rawData?.flowEntryId ?? null,
    flowConfig: rawData?.flowConfig ?? null,
    // Back-compat: legacy components saved before the rename UI never had
    // this field. ensureCreationOutputPlaceholder mints a fresh unique
    // key so they get a stable, unique placeholder on first edit.
    creationOutputPlaceholder: ensureCreationOutputPlaceholder(
      'form_step',
      rawData?.creationOutputPlaceholder,
    ),
  }),
  extraActions: (set, get) => ({
    setFields: (fields) => set({
      data: {
        ...get().data,
        fields: Array.isArray(fields) ? fields.map(stripField).filter(Boolean) : [],
      },
    }),
    setTitle: (title) => set({ data: { ...get().data, title } }),
    setDescription: (description) => set({ data: { ...get().data, description } }),
    setAccessPolicy: (accessPolicy) => set({ data: { ...get().data, accessPolicy } }),
    setResponseMode: (responseMode) => set({ data: { ...get().data, responseMode } }),
    setFormSettings: (formSettings) => set({
      data: { ...get().data, formSettings: { ...get().data.formSettings, ...formSettings } },
    }),
    setStepperTitle: (stepperTitle) => set({ data: { ...get().data, stepperTitle } }),
    setFlowSettings: ({ flowEntryId, flowConfig }) => set({
      data: { ...get().data, flowEntryId: flowEntryId ?? null, flowConfig: flowConfig ?? null },
    }),
    setFlowConfig: (flowConfig) => set({ data: { ...get().data, flowConfig } }),
    setCreationOutputPlaceholderKey: (key) => set({
      data: {
        ...get().data,
        creationOutputPlaceholder: {
          ...(get().data.creationOutputPlaceholder || {}),
          key,
        },
      },
    }),
    addField: () => {
      const fields = [...get().data.fields];
      const order = fields.length;
      fields.push(makeDefaultField('text', order));
      set({ data: { ...get().data, fields } });
    },
    updateField: (index, updates) => {
      const fields = [...get().data.fields];
      fields[index] = stripField({ ...fields[index], ...updates }) || fields[index];
      set({ data: { ...get().data, fields } });
    },
    removeField: (index) => {
      const fields = get().data.fields.filter((_, i) => i !== index);
      set({ data: { ...get().data, fields } });
    },
    reorderFields: (fromIndex, toIndex) => {
      const fields = [...get().data.fields];
      const [moved] = fields.splice(fromIndex, 1);
      fields.splice(toIndex, 0, moved);
      fields.forEach((f, i) => { f.order = i; });
      set({ data: { ...get().data, fields } });
    },
  }),
});

export { getFormStepStore };
export default defaultStore;
